import { X, Sparkles, Zap, Lock, Crown } from 'lucide-react';
import { useAiCredits } from '../../hooks/useAiCredits';

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureName?: string;
}

export function PaywallModal({ isOpen, onClose, featureName = 'AI features' }: PaywallModalProps) {
  const { used, limit } = useAiCredits();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Gradient Header */}
        <div className="bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 px-6 py-8 text-center relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full opacity-20">
            <div className="absolute top-4 left-8 w-12 h-12 bg-white rounded-full blur-xl" />
            <div className="absolute bottom-4 right-12 w-16 h-16 bg-yellow-300 rounded-full blur-2xl" />
            <div className="absolute top-1/2 right-4 w-8 h-8 bg-pink-400 rounded-full blur-lg" />
          </div>

          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="relative">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-4 backdrop-blur-sm">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">AI Credits Exhausted</h2>
            <p className="text-white/80 text-sm">
              You've used all your free AI credits
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Usage display */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-600">Credits Used</span>
              <span className="text-sm font-bold text-gray-900">{used} / {limit}</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-full transition-all"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {/* Feature they tried to use */}
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-100 rounded-xl mb-6">
            <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                You tried to use: {featureName}
              </p>
              <p className="text-xs text-amber-600 mt-1">
                This feature requires AI credits
              </p>
            </div>
          </div>

          {/* What's included */}
          <div className="space-y-3 mb-6">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-500" />
              AI features include:
            </h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                Voice item extraction
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                AI image generation
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                Smart title & description generation
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                Image analysis & auto-fill
              </li>
            </ul>
          </div>

          {/* Upgrade CTA */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-yellow-300" />
              <span className="font-bold text-white">Want more credits?</span>
            </div>
            <p className="text-white/80 text-sm mb-3">
              Contact us to unlock unlimited AI features
            </p>
            <a
              href="mailto:support@findmystuff.ai?subject=Upgrade%20AI%20Credits"
              className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Contact Support
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t">
          <button
            onClick={onClose}
            className="w-full py-2.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
